import { MutableRefObject, useCallback, useState } from 'react';

import { ModelViewerElement } from '@google/model-viewer';

import { generateNanoId } from '../../../../../../../utils/generateNanoId';
import { ModelViewerScreenshotHandle } from '../../../../../../../helpers/ModelViewer/hooks/useModelViewerScreenshot';
import { useCreateLifestyleGeneration } from '../../../../../../lifestyleGenerations/hooks/useCreateLifestyleGeneration';
import { LIFESTYLE_GENERATION_POLLING_INTERVAL } from '../../../../../../lifestyleGenerations/lifestyleGenerationsConstants';
import { LifestyleGenerationsBffUrl } from '../../../../../../lifestyleGenerations/LifestyleGenerationsBffUrl';
import {
  CreateLifestyleGenerationInput,
  FetchLifestyleGenerationResponse,
  LifestyleGenerationMode,
  LifestyleGenerationStatus,
  LifestyleGenerationType
} from '../../../../../../lifestyleGenerations/lifestyleGenerationsTypes';
import { RenderGenerationIncomingPayload } from '../../../../../../renderGenerations/renderGenerationsTypes';

import { useReferenceFilesUpload } from './useReferenceFilesUpload';
import { useScreenshotUpload } from './useScreenshotUpload';
import {
  GenerationPollResult,
  useGenerationPolling
} from './useGenerationPolling';
import { useCurrentUser } from '../../../../../../../auth/hooks/useAuth';
import { ModelViewerPermissions } from '../../../../../modelViewerConstants';
import {
  MODEL_VIEWER_ASPECT_RATIO_AUTO,
  ModelViewerAspectRatioSelection,
  ModelViewerAspectRatioValue,
  ModelViewerImageSizeValue,
  modelViewerAspectRatioValues
} from '../../modelViewerAspectRatioConstants';
import { resolveModelViewerAspectRatio } from '../../utils/resolveModelViewerAspectRatio';

interface UseModelViewerLifestyleGenerationOptions {
  modelViewerEl: ModelViewerElement | null;
  screenshotRef: MutableRefObject<ModelViewerScreenshotHandle | null>;
  backgroundMode: string;
  backgroundCssValue: string;
  // Sent verbatim as aspect_ratio/image_size — the backend resolves the
  // output resolution from this pair. requested_width/requested_height are
  // still measured from the (ratio-shaped) viewer box at request time, for
  // backward compatibility only.
  // May be "auto": resolved to the nearest preset from the live viewer box at
  // request time, so the outgoing request never carries "auto" and the
  // screenshot is cropped to the resolved ratio.
  aspectRatio: ModelViewerAspectRatioSelection;
  imageSize: ModelViewerImageSizeValue;
  modelId?: string | null;
  buildIncomingPayload?: (
    targetAspectRatio?: ModelViewerAspectRatioValue
  ) => RenderGenerationIncomingPayload | null;
}

export interface LifestyleGenerationItem {
  kind: 'lifestyle';
  createdOrder: number;
  localId: string;
  serverId?: string;
  status: LifestyleGenerationStatus;
  prompt: string;
  mode: LifestyleGenerationMode;
  selectedCommand: LifestyleGenerationMode | null;
  referenceFileUrls?: string[];
  cameraOrbit?: string;
  cameraTarget?: string;
  fieldOfView?: string;
  resultPreviewUrl?: string;
  resultFullUrl?: string;
  errorMessage?: string;
  sourceLocalId?: string;
}

const GEMINI_PROVIDER = 'gemini';
const GEMINI_PROVIDER_MODEL = 'gemini-3.1-flash-image-preview';

function mapLifestyleGenerationPollResponse(
  data: FetchLifestyleGenerationResponse
): GenerationPollResult<LifestyleGenerationStatus> {
  if (data.status === LifestyleGenerationStatus.Success) {
    return {
      status: LifestyleGenerationStatus.Success,
      resultPreviewUrl: data.results[0]?.image_result_preview,
      resultFullUrl: data.results[0]?.image_result,
      isSuccess: true
    };
  }

  if (data.status === LifestyleGenerationStatus.Failed) {
    return {
      status: LifestyleGenerationStatus.Failed,
      errorMessage: data.error_message,
      isFailed: true
    };
  }

  return { status: data.status };
}

function useModelViewerLifestyleGeneration({
  modelViewerEl,
  screenshotRef,
  backgroundMode,
  backgroundCssValue,
  aspectRatio,
  imageSize,
  modelId,
  buildIncomingPayload
}: UseModelViewerLifestyleGenerationOptions) {
  const { createLifestyleGeneration } = useCreateLifestyleGeneration();
  const currentUser = useCurrentUser();
  const hasChatgptProvider = currentUser?.hasPermissions(
    ModelViewerPermissions.READ_MODEL_VIEWER_CHATGPT_PROVIDER
  );

  const [generations, setGenerations] = useState<LifestyleGenerationItem[]>([]);
  const [activeViewId, setActiveViewId] = useState<string>('model');
  const [prompt, setPrompt] = useState('');
  const [generationMode, setGenerationMode] = useState<LifestyleGenerationMode>(
    LifestyleGenerationMode.Lifestyle
  );
  const [selectedCommand, setSelectedCommandState] =
    useState<LifestyleGenerationMode | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [pendingEditRestore, setPendingEditRestore] = useState<{
    prompt: string;
    referenceFileUrls?: string[];
  } | null>(null);

  const {
    referenceFiles,
    referenceFileUrls,
    isUploadingReference,
    applyReferenceFiles,
    handleReferenceFilesChange,
    handleRemoveReference,
    restoreReferences
  } = useReferenceFilesUpload();

  const { uploadScreenshotAsPromise } = useScreenshotUpload();

  const handlePollTick = useCallback(
    (
      localId: string,
      result: GenerationPollResult<LifestyleGenerationStatus>
    ) => {
      const generationResult = {
        status: result.status,
        resultPreviewUrl: result.resultPreviewUrl,
        resultFullUrl: result.resultFullUrl,
        errorMessage: result.errorMessage
      };

      setGenerations((prev) =>
        prev.map((g) => {
          if (g.localId !== localId) return g;
          if (
            g.status === generationResult.status &&
            generationResult.status !== LifestyleGenerationStatus.Success &&
            generationResult.status !== LifestyleGenerationStatus.Failed
          ) {
            return g;
          }
          return { ...g, ...generationResult };
        })
      );
    },
    []
  );

  const { startPolling, stopPolling } = useGenerationPolling({
    buildShowUrl: LifestyleGenerationsBffUrl.show,
    mapResponse: mapLifestyleGenerationPollResponse,
    intervalMs: LIFESTYLE_GENERATION_POLLING_INTERVAL,
    onTick: handlePollTick,
    onSuccess: setActiveViewId
  });

  const handleGenerate = useCallback(async () => {
    if (isGenerating || isUploadingReference) return;

    setIsGenerating(true);
    setGenerateError(null);

    // Read camera state synchronously before any async work.
    // getCameraOrbit/getCameraTarget/getFieldOfView return the actual rendered position
    // (from internal $lastSpherical), not the goal attribute string which does NOT update
    // when the user interacts with the viewer.
    const orbitPos = (modelViewerEl as any)?.getCameraOrbit?.() as
      | { theta: number; phi: number; radius: number; toString(): string }
      | undefined;
    const targetPos = (modelViewerEl as any)?.getCameraTarget?.() as
      | { x: number; y: number; z: number; toString(): string }
      | undefined;
    const fovDeg = (modelViewerEl as any)?.getFieldOfView?.() as
      | number
      | undefined;
    const cameraOrbit = orbitPos?.toString();
    const cameraTarget = targetPos?.toString();
    const fieldOfView = fovDeg != null ? `${fovDeg}deg` : undefined;

    // viewerRect already reflects the aspect-ratio frame (the <model-viewer>
    // is w-full/h-full inside the ratio-constrained box), or the full box in
    // auto mode — the same measurement drives the legacy
    // requested_width/requested_height pair and the auto resolution.
    const viewerRect = modelViewerEl?.getBoundingClientRect();
    const resolvedAspectRatio = resolveModelViewerAspectRatio(
      aspectRatio,
      viewerRect
    );
    // Only auto needs the screenshot cropped: other modes are already
    // CSS-constrained to the preset.
    const resolvedPreset =
      aspectRatio === MODEL_VIEWER_ASPECT_RATIO_AUTO
        ? modelViewerAspectRatioValues[resolvedAspectRatio]
        : undefined;
    const requestedWidth = viewerRect
      ? Math.round(viewerRect.width)
      : undefined;
    const requestedHeight = viewerRect
      ? Math.round(viewerRect.height)
      : undefined;

    const modelDims = (modelViewerEl as any)?.getDimensions?.() as
      | { x: number; y: number; z: number }
      | undefined;
    const METERS_TO_CM = 100;
    const sizeX =
      modelDims != null ? +(modelDims.x * METERS_TO_CM).toFixed(2) : undefined;
    const sizeY =
      modelDims != null ? +(modelDims.y * METERS_TO_CM).toFixed(2) : undefined;
    const sizeZ =
      modelDims != null ? +(modelDims.z * METERS_TO_CM).toFixed(2) : undefined;

    const localId = generateNanoId();
    setGenerations((prev) => [
      {
        kind: 'lifestyle',
        createdOrder: Date.now(),
        localId,
        status: LifestyleGenerationStatus.Pending,
        prompt,
        mode: generationMode,
        selectedCommand,
        referenceFileUrls:
          referenceFileUrls.length > 0 ? referenceFileUrls : undefined,
        cameraOrbit,
        cameraTarget,
        fieldOfView
      },
      ...prev
    ]);
    setActiveViewId(localId);

    // Product mode generates from a product model_id + scene metadata
    // (incoming_payload) instead of a screenshot subject_image_url. It shares
    // the same lifestyle endpoint, polling, and sidebar, but skips the
    // screenshot/upload step entirely. Mirrors the regular Lifestyle command:
    // a reference image picks lifestyle_by_reference, otherwise by_prompt.
    const isProductMode =
      generationMode === LifestyleGenerationMode.ProductLifestyle;

    if (isProductMode) {
      try {
        const generationType =
          referenceFileUrls.length > 0
            ? LifestyleGenerationType.LifestyleByReference
            : LifestyleGenerationType.LifestyleByPrompt;

        const input: CreateLifestyleGenerationInput = {
          generation_type: generationType,
          model_id: modelId ?? null,
          user_id: currentUser?.id ?? null,
          incoming_payload:
            (buildIncomingPayload?.(resolvedAspectRatio) as unknown as Record<
              string,
              unknown
            > | null) ?? null,
          prompt: prompt || null,
          requested_width: requestedWidth,
          requested_height: requestedHeight,
          aspect_ratio: resolvedAspectRatio,
          image_size: imageSize,
          size_x: sizeX,
          size_y: sizeY,
          size_z: sizeZ,
          ...(generationType ===
            LifestyleGenerationType.LifestyleByReference && {
            lifestyle_image_url: referenceFileUrls
          }),
          ...(hasChatgptProvider && {
            preferred_provider: GEMINI_PROVIDER,
            preferred_model: GEMINI_PROVIDER_MODEL
          })
        };

        const response = await createLifestyleGeneration(input);

        setGenerations((prev) =>
          prev.map((g) =>
            g.localId === localId ? { ...g, serverId: response.id } : g
          )
        );
        startPolling(localId, response.id);
      } catch (err) {
        const message =
          (err as any)?.fullMessages?.[0] ||
          (err as any)?.message ||
          'Generation failed';
        setGenerateError(message);
        setGenerations((prev) =>
          prev.map((g) =>
            g.localId === localId
              ? {
                  ...g,
                  status: LifestyleGenerationStatus.Failed,
                  errorMessage: message
                }
              : g
          )
        );
        console.error('[product lifestyle generation]', err);
      } finally {
        setIsGenerating(false);
      }
      return;
    }

    try {
      if (!screenshotRef.current) throw new Error('Screenshot API not ready');
      // In auto mode the viewer box is unconstrained, so crop the capture to
      // the resolved ratio — the upload matches aspect_ratio either way.
      const { blob } = await screenshotRef.current.getScreenshot({
        cropToAspectRatio: resolvedPreset
      });
      const screenshotFile = new File([blob], 'screenshot.png', {
        type: 'image/png'
      });
      const subjectImageUrl = await uploadScreenshotAsPromise(screenshotFile);

      const generationType: LifestyleGenerationType =
        generationMode === LifestyleGenerationMode.Lifestyle
          ? referenceFileUrls.length > 0
            ? LifestyleGenerationType.LifestyleByReference
            : LifestyleGenerationType.LifestyleByPrompt
          : generationMode === LifestyleGenerationMode.Silo
          ? LifestyleGenerationType.Silo
          : LifestyleGenerationType.CloseUp;

      const input: CreateLifestyleGenerationInput = {
        generation_type: generationType,
        subject_image_url: subjectImageUrl,
        ...(modelId && {
          model_id: modelId,
          incoming_payload:
            (buildIncomingPayload?.(resolvedAspectRatio) as unknown as Record<
              string,
              unknown
            > | null) ?? null
        }),
        user_id: currentUser?.id ?? null,
        prompt: prompt || null,
        requested_width: requestedWidth,
        requested_height: requestedHeight,
        aspect_ratio: resolvedAspectRatio,
        image_size: imageSize,
        size_x: sizeX,
        size_y: sizeY,
        size_z: sizeZ,
        ...(generationType === LifestyleGenerationType.LifestyleByReference && {
          lifestyle_image_url: referenceFileUrls
        }),
        ...(generationType === LifestyleGenerationType.Silo &&
          backgroundMode !== 'default' &&
          backgroundMode !== 'image' && {
            silo_background_hex: backgroundCssValue || null
          }),
        ...(hasChatgptProvider && {
          preferred_provider: GEMINI_PROVIDER,
          preferred_model: GEMINI_PROVIDER_MODEL
        })
      };

      const response = await createLifestyleGeneration(input);

      setGenerations((prev) =>
        prev.map((g) =>
          g.localId === localId ? { ...g, serverId: response.id } : g
        )
      );
      startPolling(localId, response.id);
    } catch (err) {
      const message =
        (err as any)?.fullMessages?.[0] ||
        (err as any)?.message ||
        'Generation failed';
      setGenerateError(message);
      setGenerations((prev) =>
        prev.map((g) =>
          g.localId === localId
            ? {
                ...g,
                status: LifestyleGenerationStatus.Failed,
                errorMessage: message
              }
            : g
        )
      );
      console.error('[lifestyle generation]', err);
    } finally {
      setIsGenerating(false);
    }
  }, [
    prompt,
    generationMode,
    selectedCommand,
    referenceFileUrls,
    isGenerating,
    isUploadingReference,
    screenshotRef,
    uploadScreenshotAsPromise,
    modelViewerEl,
    createLifestyleGeneration,
    startPolling,
    hasChatgptProvider,
    currentUser?.id,
    backgroundMode,
    backgroundCssValue,
    aspectRatio,
    imageSize,
    modelId,
    buildIncomingPayload
  ]);

  const handleEditGenerate = useCallback(
    async (
      sourceItem: LifestyleGenerationItem,
      editPrompt: string,
      editReferenceFileUrls?: string[]
    ) => {
      if (isGenerating) return;
      if (!sourceItem.resultFullUrl) return;

      setIsGenerating(true);
      setGenerateError(null);

      // See handleGenerate: the legacy requested_width/requested_height pair
      // mirrors the ratio-framed viewer box, for backward compatibility only.
      const viewerRect = modelViewerEl?.getBoundingClientRect();
      const resolvedAspectRatio = resolveModelViewerAspectRatio(
        aspectRatio,
        viewerRect
      );
      const requestedWidth = viewerRect
        ? Math.round(viewerRect.width)
        : undefined;
      const requestedHeight = viewerRect
        ? Math.round(viewerRect.height)
        : undefined;

      const localId = generateNanoId();
      setGenerations((prev) => [
        {
          kind: 'lifestyle',
          createdOrder: Date.now(),
          localId,
          status: LifestyleGenerationStatus.Pending,
          prompt: editPrompt,
          mode: LifestyleGenerationMode.Edit,
          selectedCommand: null,
          referenceFileUrls: editReferenceFileUrls?.length
            ? editReferenceFileUrls
            : undefined,
          sourceLocalId: sourceItem.localId
        },
        ...prev
      ]);
      setActiveViewId(localId);

      try {
        const input: CreateLifestyleGenerationInput = {
          generation_type: LifestyleGenerationType.EditGeneratedImage,
          subject_image_url: sourceItem.resultFullUrl,
          user_id: currentUser?.id ?? null,
          prompt: editPrompt || null,
          requested_width: requestedWidth,
          requested_height: requestedHeight,
          aspect_ratio: resolvedAspectRatio,
          image_size: imageSize,
          ...(!!editReferenceFileUrls?.length && {
            lifestyle_image_url: editReferenceFileUrls
          }),
          ...(hasChatgptProvider && {
            preferred_provider: GEMINI_PROVIDER,
            preferred_model: GEMINI_PROVIDER_MODEL
          })
        };

        const response = await createLifestyleGeneration(input);

        setGenerations((prev) =>
          prev.map((g) =>
            g.localId === localId ? { ...g, serverId: response.id } : g
          )
        );
        startPolling(localId, response.id);
      } catch (err) {
        const message =
          (err as any)?.fullMessages?.[0] ||
          (err as any)?.message ||
          'Generation failed';
        setGenerateError(message);
        setGenerations((prev) =>
          prev.map((g) =>
            g.localId === localId
              ? {
                  ...g,
                  status: LifestyleGenerationStatus.Failed,
                  errorMessage: message
                }
              : g
          )
        );
        console.error('[edit generation]', err);
      } finally {
        setIsGenerating(false);
      }
    },
    [
      isGenerating,
      modelViewerEl,
      createLifestyleGeneration,
      startPolling,
      hasChatgptProvider,
      currentUser?.id,
      aspectRatio,
      imageSize
    ]
  );

  const handleAdjust = useCallback(
    (item: LifestyleGenerationItem) => {
      if (item.mode === LifestyleGenerationMode.Edit && item.sourceLocalId) {
        setPendingEditRestore({
          prompt: item.prompt,
          referenceFileUrls: item.referenceFileUrls
        });
        setActiveViewId(item.sourceLocalId);
        return;
      }
      setPrompt(item.prompt);
      if (item.selectedCommand) {
        setSelectedCommandState(item.selectedCommand);
        setGenerationMode(item.selectedCommand);
      } else {
        setSelectedCommandState(null);
        setGenerationMode(LifestyleGenerationMode.Lifestyle);
      }
      restoreReferences(item.referenceFileUrls ?? []);
      setActiveViewId('model');
      if (modelViewerEl) {
        if (item.cameraTarget)
          (modelViewerEl as any).cameraTarget = item.cameraTarget;
        if (item.cameraOrbit)
          (modelViewerEl as any).cameraOrbit = item.cameraOrbit;
        if (item.fieldOfView)
          (modelViewerEl as any).fieldOfView = item.fieldOfView;
        (modelViewerEl as any).jumpCameraToGoal?.();
      }
    },
    [modelViewerEl, restoreReferences]
  );

  const dismissGeneration = useCallback(
    (localId: string) => {
      stopPolling(localId);
      setGenerations((prev) => prev.filter((g) => g.localId !== localId));
      setActiveViewId((prev) => (prev === localId ? 'model' : prev));
    },
    [stopPolling]
  );

  const handleRemoveGeneration = dismissGeneration;
  const handleStopGeneration = dismissGeneration;

  const setSelectedCommand = useCallback((mode: LifestyleGenerationMode) => {
    setSelectedCommandState(mode);
    setGenerationMode(mode);
  }, []);

  const clearSelectedCommand = useCallback(() => {
    setSelectedCommandState(null);
    setGenerationMode(LifestyleGenerationMode.Lifestyle);
  }, []);

  const handleReferenceFileDrop = useCallback(
    (file: File) => applyReferenceFiles([file]),
    [applyReferenceFiles]
  );

  return {
    generations,
    activeViewId,
    setActiveViewId,
    prompt,
    setPrompt,
    generationMode,
    selectedCommand,
    setSelectedCommand,
    clearSelectedCommand,
    referenceFiles,
    referenceFileUrls,
    isUploadingReference,
    isGenerating,
    generateError,
    handleReferenceFilesChange,
    handleReferenceFileDrop,
    handleRemoveReference,
    handleGenerate,
    handleEditGenerate,
    handleAdjust,
    handleRemoveGeneration,
    handleStopGeneration,
    pendingEditRestore,
    clearPendingEditRestore: () => setPendingEditRestore(null)
  };
}

export default useModelViewerLifestyleGeneration;
